import type { AiPersonality } from './ai/personality.js';

// Branded ID types — prevents accidentally passing a PlayerId where a TerritoryId is expected.
export type TerritoryId = string & { readonly __brand: 'TerritoryId' };
export type PlayerId = string & { readonly __brand: 'PlayerId' };
export type RegionId = string & { readonly __brand: 'RegionId' };

export const territoryId = (s: string): TerritoryId => s as TerritoryId;
export const playerId = (s: string): PlayerId => s as PlayerId;
export const regionId = (s: string): RegionId => s as RegionId;

// --- Map (static data) ---
export interface Territory {
  readonly id: TerritoryId;
  readonly name: string;
  readonly adjacent: readonly TerritoryId[];
  readonly region: RegionId;
  readonly position: { readonly x: number; readonly y: number };
}

export interface Region {
  readonly id: RegionId;
  readonly name: string;
  readonly territoryBonus: number;
}

export interface MapDefinition {
  readonly id: string;
  readonly name: string;
  readonly territories: readonly Territory[];
  readonly regions: readonly Region[];
  /**
   * Visual tile size in design pixels. Optional — when omitted, the
   * presentation layer falls back to the default `TERRITORY_SIZE`
   * (80px). Larger maps that pack many tiles into the canvas can
   * declare a smaller value (e.g. Terra at 64) so flush adjacencies
   * actually render flush instead of overlapping. Tile positions
   * MUST be spaced by this same value within a region.
   */
  readonly tileSize?: number;
}

// --- Players ---
export type PlayerKind = 'human' | 'ai';

export interface Player {
  readonly id: PlayerId;
  readonly name: string;
  readonly kind: PlayerKind;
  readonly color: string;
  readonly eliminated: boolean;
  /**
   * Team affiliation, 1..4. Default = unique-per-seat (FFA — each
   * player on their own team). Players sharing a team can't attack
   * each other, can fortify through each other's territory, and win
   * jointly when all opposing teams are eliminated.
   */
  readonly team: number;
  // Set on AI seats only. Drives attack threshold, threat weighting,
  // region weighting, aggression bias, and top-K randomness for that
  // seat. Human seats omit this entirely (with exactOptionalPropertyTypes
  // we never assign undefined). The AI falls back to DEFAULT_PERSONALITY
  // when missing — preserves legacy / test-built states.
  readonly personality?: AiPersonality;
}

// --- Runtime per-territory state ---
export interface TerritoryState {
  readonly owner: PlayerId;
  readonly units: number;
}

// --- Turn flow ---
export type TurnPhase = 'deploy' | 'attack' | 'fortify';

// After a successful conquest the attacker MUST resolve an advance before any
// other action. Modeled as a discriminated union so the reducer/UI can't forget.
export type AttackSubPhase =
  | { readonly kind: 'free' }
  | {
      readonly kind: 'awaitingAdvance';
      readonly from: TerritoryId;
      readonly to: TerritoryId;
      readonly minUnits: number; // = attacking dice count
      readonly maxUnits: number; // = units(from) - 1
    };

export type GameStatus =
  | { readonly kind: 'inProgress' }
  | {
      readonly kind: 'victory';
      // Primary winner — for FFA this is THE winner; for team wins
      // it's the player who landed the killing blow (the
      // currentPlayer when the last opposing team got eliminated).
      readonly winner: PlayerId;
      // The winning team. All living players on this team are
      // co-winners; UI uses this to render team victory copy when
      // there are 2+ survivors on the same team.
      readonly winningTeam: number;
    };

/**
 * One deploy action made during the current player's deploy phase.
 * Used to power "undo last deploy" — pop the last record, reverse
 * the unit shift, refund the reinforcements. Cleared when the
 * deploy phase ends.
 */
export interface DeployRecord {
  readonly territory: TerritoryId;
  readonly units: number;
}

export interface TurnContext {
  readonly currentPlayer: PlayerId;
  readonly phase: TurnPhase;
  readonly attack: AttackSubPhase;
  readonly reinforcementsRemaining: number;
  readonly fortifyUsed: boolean;
  readonly lastCombat?: CombatResult;
  /**
   * Deploys made during the CURRENT deploy phase. Append on each
   * successful deploy; pop on undo; clear at phase end. Empty in
   * any phase other than deploy.
   */
  readonly deploysThisTurn: readonly DeployRecord[];
}

/**
 * One entry in the battle-log history. Captures the action that ran,
 * the events it produced, and metadata for rendering or replay. Both
 * the `action` and `events` fields are persisted because:
 *
 *  - `action` is what replay needs to deterministically reconstruct
 *    the game state from the initial seed. Walk history → apply each
 *    action to a fresh state → reach the same final position.
 *  - `events` is the render-ready summary the battle-log UI displays.
 *    Avoids re-deriving on every paint.
 *
 * `id` is monotonically increasing across the entire game, never
 * reused — this is the cursor a future multiplayer client uses to
 * say "send me everything after id N". Survives history trimming.
 */
export interface LogEntry {
  readonly id: number;
  readonly turnNumber: number;
  readonly actor: PlayerId | null;
  readonly action: Action;
  readonly events: readonly GameEvent[];
}

// --- The whole world. Plain data. Fully serializable. ---
export interface GameState {
  readonly mapId: string;
  readonly turnNumber: number;
  readonly players: readonly Player[];
  readonly playerOrder: readonly PlayerId[];
  readonly territories: Readonly<Record<TerritoryId, TerritoryState>>;
  readonly turn: TurnContext;
  readonly status: GameStatus;
  readonly rngSeed: number;
  // Append-only list of players in the order they were eliminated. Drives
  // the post-game podium (3rd = first eliminated, 2nd = last eliminated,
  // 1st = the survivor). Empty until someone loses their last territory.
  readonly eliminationOrder: readonly PlayerId[];
  /**
   * Battle-log history. Each entry captures one action + its events.
   * Capped to retain the last 100 attack actions; older entries
   * trimmed on append. Powers the in-game battle log and is the
   * foundation for future replay / multiplayer-catch-up features.
   */
  readonly history: readonly LogEntry[];
  /**
   * Per-player count of consecutive auto-skipped turns (v0.9.9
   * Hourglass). Resets to 0 when that player takes any non-autoSkip
   * action. Triggers full forfeit via `forfeit.ts` at 3.
   *
   * Missing key reads as 0 — important for backward-compat with
   * pre-0.9.9 game state JSON persisted in `games.state`.
   * The reducer always reads `state.consecutiveAutoSkips[id] ?? 0`.
   */
  readonly consecutiveAutoSkips: Readonly<Record<PlayerId, number>>;
}

// --- Actions ---
export type Action =
  | {
      readonly type: 'deploy';
      readonly playerId: PlayerId;
      readonly territory: TerritoryId;
      readonly units: number;
    }
  | {
      readonly type: 'attack';
      readonly playerId: PlayerId;
      readonly from: TerritoryId;
      readonly to: TerritoryId;
      readonly attackingDice: 1 | 2 | 3;
    }
  | {
      readonly type: 'advance';
      readonly playerId: PlayerId;
      readonly from: TerritoryId;
      readonly to: TerritoryId;
      readonly units: number;
    }
  | {
      readonly type: 'fortify';
      readonly playerId: PlayerId;
      readonly from: TerritoryId;
      readonly to: TerritoryId;
      readonly units: number;
    }
  | { readonly type: 'endPhase'; readonly playerId: PlayerId }
  | { readonly type: 'endTurn'; readonly playerId: PlayerId }
  | { readonly type: 'undoLastDeploy'; readonly playerId: PlayerId }
  | {
      /**
       * Server-only action — generated by the 24hr cron sweep, never
       * dispatched from the client. Skips the entire current turn for
       * `playerId` (no deploy, no attack, no fortify) and advances to
       * the next non-eliminated player. Triggers full forfeit when
       * the player's `consecutiveAutoSkips` would reach 3.
       * `turnNumber` guards against stale cron actions racing real
       * user actions — validation rejects if it doesn't match the
       * current state's turn. v0.9.9 Hourglass.
       */
      readonly type: 'autoSkip';
      readonly playerId: PlayerId;
      readonly turnNumber: number;
    };

// --- Combat ---
export interface CombatRoll {
  readonly attackerDice: readonly number[];
  readonly defenderDice: readonly number[];
}

export interface CombatResult {
  readonly from: TerritoryId;
  readonly to: TerritoryId;
  readonly roll: CombatRoll;
  readonly attackerLosses: number;
  readonly defenderLosses: number;
  readonly conquered: boolean;
}

// --- Action validation result ---
export type ActionValidation =
  | { readonly ok: true }
  | { readonly ok: false; readonly reason: string };

// --- Reducer output ---
export interface ApplyResult {
  readonly state: GameState;
  readonly events: readonly GameEvent[];
}

export type GameEvent =
  | {
      readonly kind: 'deployed';
      readonly playerId: PlayerId;
      readonly territory: TerritoryId;
      readonly units: number;
    }
  | { readonly kind: 'combatResolved'; readonly result: CombatResult }
  | {
      readonly kind: 'territoryCaptured';
      readonly territory: TerritoryId;
      readonly from: PlayerId;
      readonly to: PlayerId;
    }
  | {
      readonly kind: 'advanced';
      readonly from: TerritoryId;
      readonly to: TerritoryId;
      readonly units: number;
    }
  | {
      readonly kind: 'fortified';
      readonly from: TerritoryId;
      readonly to: TerritoryId;
      readonly units: number;
    }
  | {
      readonly kind: 'deployUndone';
      readonly territory: TerritoryId;
      readonly units: number;
    }
  | { readonly kind: 'phaseChanged'; readonly phase: TurnPhase }
  | {
      readonly kind: 'turnEnded';
      readonly nextPlayer: PlayerId;
      readonly turnNumber: number;
    }
  | { readonly kind: 'playerEliminated'; readonly playerId: PlayerId }
  | { readonly kind: 'gameWon'; readonly winner: PlayerId }
  | {
      /**
       * Fires when a player's turn is auto-skipped by the 24hr cron
       * (v0.9.9 Hourglass). `consecutiveCount` is 1, 2, or 3 — also
       * fires on the 3rd skip immediately before `turnForfeited`.
       * Drives the inbox AutoSkipToast and battle-log entry.
       */
      readonly kind: 'turnSkipped';
      readonly playerId: PlayerId;
      readonly turnNumber: number;
      readonly consecutiveCount: number;
    }
  | {
      /**
       * Fires after the 3rd consecutive auto-skip triggers full
       * forfeit via `applyForfeit`. The accompanying
       * `playerEliminated` event(s) come from the forfeit chain.
       * `consecutiveCount` is always 3 — included for debug /
       * historical clarity in the action log.
       */
      readonly kind: 'turnForfeited';
      readonly playerId: PlayerId;
      readonly consecutiveCount: number;
    };

// Exhaustiveness helper — TypeScript will flag missing union cases as a
// compile error if a switch falls through to this.
export function assertNever(x: never): never {
  throw new Error(`Unexpected discriminant: ${JSON.stringify(x)}`);
}
